import { ReactElement, useMemo, useState } from 'react';
import { CompanyUser } from '@/api';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UIText
} from '@/ui';
import { useUpdateCompanyUserAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';

type PowerMode = CompanyUser['powerMode'];

type PowerModeSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

const POWER_MODES: PowerMode[] = ['NORMAL', 'LOW'];

/**
 * The device power mode controls how aggressively the Safepoint app runs in
 * the background — lower modes save battery at the cost of location accuracy.
 */
export function MemberPowerModeSettingsSection(
    props: PowerModeSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_power_mode_settings_section.toast_updated'
        )
    });

    const [isEditing, setIsEditing] = useState(false);
    const [powerMode, setPowerMode] = useState<PowerMode>(
        companyUser.powerMode
    );

    const options = useMemo<UISelectValue[]>(
        () =>
            POWER_MODES.map((mode) => ({
                value: mode,
                label: t(
                    `components.member_power_mode_settings_section.mode_${mode}`
                )
            })),
        [t]
    );

    const selectedOption = options.find((opt) => opt.value === powerMode);

    function resetForm() {
        setPowerMode(companyUser.powerMode);
        setIsEditing(false);
    }

    function onSubmit() {
        updateCompanyUser
            .updateCompanyUser(companyUser.id, { powerMode })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UISection>
            <UISectionHeader
                title={t('components.member_power_mode_settings_section.title')}
                rightButtons={
                    isEditing
                        ? []
                        : [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  disabled: !canEdit,
                                  disabledTooltip: editDisabledReason,
                                  onClick: () => setIsEditing(true)
                              }
                          ]
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 16
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        <UIText>
                            {t(
                                'components.member_power_mode_settings_section.mode_label'
                            )}
                        </UIText>
                        <UIText type="default2">
                            {t(
                                'components.member_power_mode_settings_section.mode_description'
                            )}
                        </UIText>
                    </div>
                    <div style={{ display: 'flex', flex: 1 }}>
                        {isEditing ? (
                            <UISelectInput
                                value={selectedOption}
                                options={options}
                                onChange={(option) => {
                                    if (option) {
                                        setPowerMode(option.value as PowerMode);
                                    }
                                }}
                                clearable={false}
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <UIText medium>
                                {selectedOption?.label ??
                                    t(
                                        'components.member_power_mode_settings_section.mode_NORMAL'
                                    )}
                            </UIText>
                        )}
                    </div>
                </div>
            </UISectionContent>
            {isEditing && (
                <UISectionFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'button',
                            loading: updateCompanyUser.loading,
                            disabled: updateCompanyUser.loading,
                            text: updateCompanyUser.loading
                                ? t('shared.labels.saving')
                                : t('shared.labels.save'),
                            onClick: onSubmit
                        },
                        {
                            text: t('shared.labels.cancel'),
                            type: 'button',
                            disabled: updateCompanyUser.loading,
                            onClick: resetForm
                        }
                    ]}
                />
            )}
        </UISection>
    );
}
